import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Trans, useTranslation } from "react-i18next";
import { useEffect } from "react";
import {
  AppButton,
  AppForm,
  AppLink,
  AppText,
  FillTheFieldErrorMessage,
  FormCheckbox,
} from "@/shared/Components";
import { appRouterPaths } from "@/services/navigation";
import { privacyPolicy, termsConditions } from "@/shared/constants/umarta-links";
import type { RegisterInitRequest } from "@/services/api";
import type { RegistrationFormValues } from "../../model";
import { getUserRegistrationSchema, mapToRegisterRequest } from "../../model";
import { FormHeader } from "../FormHeader";
import { FormFields } from "./FormFields";
import styles from "./styles.module.scss";

type Props = {
  onSubmit: (requestPayload: RegisterInitRequest) => void;
  errorMessage?: string;
};

export const UserRegistrationForm = ({ onSubmit, errorMessage }: Props) => {
  const { t } = useTranslation();

  const formApi = useForm({
    resolver: yupResolver(getUserRegistrationSchema()),
    defaultValues: { isAgree: false },
  });

  const handleSubmit = (data: RegistrationFormValues) => {
    onSubmit(mapToRegisterRequest(data));
  };

  useEffect(() => {
    if (errorMessage) {
      formApi.setError("email", { message: " " });
      formApi.setError("phoneNumber", { message: " " });
    }
  }, [errorMessage]);

  return (
    <div>
      <AppForm formApi={formApi} onSubmit={handleSubmit} className={styles.registration}>
        <FormHeader
          title={t("registrationPage.userInfoStep.createNew")}
          subtitle={t("registrationPage.userInfoStep.alreadyMember")}
          buttonText={t("registrationPage.userInfoStep.logIn")}
          path={appRouterPaths.logIn}
        />
        <FillTheFieldErrorMessage name={"registrationForm"} />
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        <FormFields />
        <FormCheckbox name="isAgree">
          <Trans
            i18nKey="registrationPage.userInfoStep.agreeToTerms"
            components={{
              terms: <AppLink href={termsConditions} target="_blank" />,
              policy: <AppLink href={privacyPolicy} target="_blank" />,
              text: <AppText type="secondary" />,
            }}
          />
        </FormCheckbox>
        <AppButton
          htmlType="submit"
          type="primary"
          disabled={!formApi.watch("isAgree")}
          className={styles.submit}
        >
          {t("registrationPage.userInfoStep.create")}
        </AppButton>
      </AppForm>
    </div>
  );
};
